import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { MaskedLine } from "@/components/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1766785368863-f2188a8c8b32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5fGVufDB8fHx8MTc4MTAzMjcyNnww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.02, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.85, 1]);

  return (
    <section ref={ref} className="relative w-full h-[100vh] min-h-[680px] overflow-hidden bg-black">
      <motion.img
        src={HERO_IMG}
        alt="Semi-truck on an American highway at golden hour"
        className="absolute inset-0 w-full h-full object-cover will-change-transform"
        style={{ y, scale }}
        initial={{ opacity: 0, scale: 1.08 }}
        animate={{ opacity: 1, scale: 1.02 }}
        transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
      />
      <motion.div className="absolute inset-0 hero-overlay" style={{ opacity: overlayOpacity }} />
      <div className="absolute inset-0 grain opacity-40" />

      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1.6 }}
        className="hidden md:flex absolute top-24 right-10 z-10 items-center gap-3 text-white/70 font-mono-label text-[10px]"
      >
        <span className="w-2 h-2 bg-[#FF4500] animate-pulse" /> DISPATCH DESK · LIVE
      </motion.div>

      <div className="relative h-full max-w-7xl mx-auto px-6 md:px-10 pt-32 md:pt-40 flex flex-col justify-center">
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.3 }}
          className="font-mono-label text-xs md:text-sm text-[#FF4500]" data-testid="hero-eyebrow"
        >
          <span className="inline-block w-8 h-px bg-[#FF4500] mr-3 align-middle" />
          CHAPTER 01 · EULESS, TX · DISPATCHING 48 STATES
        </motion.p>

        <h1 className="mt-8 font-display font-bold text-white text-5xl sm:text-6xl md:text-[7rem] leading-[0.9] max-w-5xl tracking-tight" data-testid="hero-headline">
          <MaskedLine delay={0.25}>We keep your</MaskedLine>
          <MaskedLine delay={0.45}>wheels loaded,</MaskedLine>
          <MaskedLine delay={0.65} className="text-[#FF4500] italic font-display">paid, and paid well.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 text-white/85 text-lg md:text-xl max-w-xl leading-relaxed" data-testid="hero-subhead"
        >
          A truck dispatching service for owner-operators and small carriers. Profitable loads, negotiated rates, efficient routes — plus every BOL, rate con and invoice handled.
          <span className="block mt-2 text-white/50 text-base">You drive. We do the rest.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a href="#quote" className="sharp-btn group relative inline-flex items-center gap-3 bg-[#FF4500] text-white px-7 py-4 font-medium text-sm hover:bg-white hover:text-black overflow-hidden" data-testid="hero-cta-quote">
            <span className="relative z-10">Sign My Truck Up</span>
            <ArrowRight size={16} className="relative z-10 transition-transform group-hover:translate-x-1" />
          </a>
          <Link to="/track" className="sharp-btn group inline-flex items-center gap-2 border border-white/40 text-white px-7 py-4 font-medium text-sm hover:bg-white hover:text-black" data-testid="hero-cta-track">
            Track a Load <ArrowUpRight size={16} className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"/>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.7 }}
          className="absolute bottom-10 left-6 md:left-10 right-6 md:right-10 grid grid-cols-3 gap-4 md:gap-12 text-white border-t border-white/20 pt-6"
        >
          {[
            { v: <>5–7<span className="text-[#FF4500]">%</span></>, l: "Flat Dispatch Fee" },
            { v: <>24<span className="text-[#FF4500]">hr</span></>, l: "Carrier Onboarding" },
            { v: <>48<span className="text-[#FF4500]">/</span>0</>, l: "States Covered" },
          ].map((s, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.9 + i * 0.12 }}
            >
              <p className="font-display font-bold text-3xl md:text-5xl leading-none">{s.v}</p>
              <p className="font-mono-label text-[10px] md:text-xs text-white/60 mt-2">{s.l}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2, duration: 0.6 }}
        className="hidden md:flex absolute bottom-40 right-10 flex-col items-center gap-3 text-white/60"
      >
        <span className="font-mono-label text-[10px] rotate-90 origin-right whitespace-nowrap">SCROLL</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          className="w-px h-10 bg-white/40"
        />
      </motion.div>
    </section>
  );
}
